<template>
  <div>
    <b-card
      no-body
      class="shadow-sm border-0"
    >
      <!-- Header -->
      <b-card-header class="d-flex justify-content-between align-items-center bg-gradient-primary text-white">
        <div class="d-flex align-items-center">
          <div
            class="icon-wrapper mr-3"
            style="background-color: rgba(255, 255, 255, 0.2);"
          >
            <feather-icon
              icon="SettingsIcon"
              size="20"
              class="text-white"
            />
          </div>
          <h5 class="mb-0">
            الملف الهندسي
          </h5>
        </div>
        <b-button
          v-if="!isEditing"
          variant="light"
          size="sm"
          @click="startEdit"
        >
          <feather-icon
            icon="EditIcon"
            size="16"
            class="mr-1"
          />
          تعديل
        </b-button>
        <div v-else>
          <b-button
            variant="light"
            size="sm"
            class="mr-2"
            @click="cancelEdit"
          >
            <feather-icon
              icon="XIcon"
              size="16"
              class="mr-1"
            />
            إلغاء
          </b-button>
          <b-button
            variant="light"
            size="sm"
            :disabled="saving"
            @click="saveProfile"
          >
            <feather-icon
              icon="SaveIcon"
              size="16"
              class="mr-1"
            />
            {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
          </b-button>
        </div>
      </b-card-header>

      <!-- Loading State -->
      <b-card-body
        v-if="loadEngineeringProfile"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          class="mb-2"
        />
        <p class="text-muted mb-0">
          جاري تحميل بيانات الملف الهندسي...
        </p>
      </b-card-body>

      <!-- Content -->
      <b-card-body v-else>
        <b-row class="g-4">
          <!-- Engineering Office Information -->
          <b-col
            cols="12"
            lg="6"
          >
            <b-card class="info-card h-100 shadow-sm border-0">
              <div class="card-icon-header mb-3">
                <div class="icon-wrapper bg-primary-light">
                  <feather-icon
                    icon="BuildingIcon"
                    size="20"
                    class="text-primary"
                  />
                </div>
                <h5 class="mb-0 font-weight-bold">
                  معلومات المكتب الهندسي
                </h5>
              </div>

              <div v-if="!isEditing">
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="FileTextIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    اسم المكتب الهندسي
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.engineering_office && engineeringProfile.engineering_office.name) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="ShieldIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    رقم الترخيص
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.engineering_office && engineeringProfile.engineering_office.license) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="PhoneIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    معلومات الاتصال
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.engineering_office && engineeringProfile.engineering_office.contact) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item">
                  <label class="info-label">
                    <feather-icon
                      icon="MapPinIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    العنوان
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.engineering_office && engineeringProfile.engineering_office.address) || 'غير محدد' }}
                  </p>
                </div>
              </div>

              <div v-else>
                <b-form-group
                  label="اسم المكتب الهندسي"
                  label-for="engineering_office_name"
                >
                  <b-form-input
                    id="engineering_office_name"
                    v-model="formData.engineering_office_name"
                    placeholder="أدخل اسم المكتب الهندسي"
                  />
                </b-form-group>
                <b-form-group
                  label="رقم الترخيص"
                  label-for="engineering_office_license"
                >
                  <b-form-input
                    id="engineering_office_license"
                    v-model="formData.engineering_office_license"
                    placeholder="أدخل رقم الترخيص"
                  />
                </b-form-group>
                <b-form-group
                  label="معلومات الاتصال"
                  label-for="engineering_office_contact"
                >
                  <b-form-input
                    id="engineering_office_contact"
                    v-model="formData.engineering_office_contact"
                    placeholder="أدخل معلومات الاتصال"
                  />
                </b-form-group>
                <b-form-group
                  label="العنوان"
                  label-for="engineering_office_address"
                >
                  <b-form-textarea
                    id="engineering_office_address"
                    v-model="formData.engineering_office_address"
                    placeholder="أدخل العنوان"
                    rows="3"
                  />
                </b-form-group>
              </div>
            </b-card>
          </b-col>

          <!-- Licensing Information -->
          <b-col
            cols="12"
            lg="6"
          >
            <b-card class="info-card h-100 shadow-sm border-0">
              <div class="card-icon-header mb-3">
                <div class="icon-wrapper bg-success-light">
                  <feather-icon
                    icon="FileTextIcon"
                    size="20"
                    class="text-success"
                  />
                </div>
                <h5 class="mb-0 font-weight-bold">
                  معلومات الترخيص
                </h5>
              </div>

              <div v-if="!isEditing">
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="HashIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    رقم الترخيص
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.licensing && engineeringProfile.licensing.license_number) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="CalendarIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    تاريخ الإصدار
                  </label>
                  <p class="info-value">
                    {{ formatDate(engineeringProfile && engineeringProfile.licensing && engineeringProfile.licensing.issue_date) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="ClockIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    تاريخ الانتهاء
                  </label>
                  <p class="info-value">
                    {{ formatDate(engineeringProfile && engineeringProfile.licensing && engineeringProfile.licensing.expiry_date) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item">
                  <label class="info-label">
                    <feather-icon
                      icon="AwardIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    الجهة المصدرة
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.licensing && engineeringProfile.licensing.authority) || 'غير محدد' }}
                  </p>
                </div>
              </div>

              <div v-else>
                <b-form-group
                  label="رقم الترخيص"
                  label-for="license_number"
                >
                  <b-form-input
                    id="license_number"
                    v-model="formData.license_number"
                    placeholder="أدخل رقم الترخيص"
                  />
                </b-form-group>
                <b-form-group
                  label="تاريخ الإصدار"
                  label-for="license_issue_date"
                >
                  <b-form-input
                    id="license_issue_date"
                    v-model="formData.license_issue_date"
                    type="date"
                  />
                </b-form-group>
                <b-form-group
                  label="تاريخ الانتهاء"
                  label-for="license_expiry_date"
                >
                  <b-form-input
                    id="license_expiry_date"
                    v-model="formData.license_expiry_date"
                    type="date"
                  />
                </b-form-group>
                <b-form-group
                  label="الجهة المصدرة"
                  label-for="license_authority"
                >
                  <b-form-input
                    id="license_authority"
                    v-model="formData.license_authority"
                    placeholder="أدخل الجهة المصدرة"
                  />
                </b-form-group>
              </div>
            </b-card>
          </b-col>

          <!-- Project Details -->
          <b-col cols="12">
            <b-card class="info-card shadow-sm border-0">
              <div class="card-icon-header mb-3">
                <div class="icon-wrapper bg-info-light">
                  <feather-icon
                    icon="InfoIcon"
                    size="20"
                    class="text-info"
                  />
                </div>
                <h5 class="mb-0 font-weight-bold">
                  تفاصيل المشروع
                </h5>
              </div>

              <div v-if="!isEditing">
                <div class="info-item mb-4">
                  <label class="info-label">
                    <feather-icon
                      icon="FileTextIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    نطاق العمل
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.project_details && engineeringProfile.project_details.scope_of_work) || 'غير محدد' }}
                  </p>
                </div>
                <div class="info-item">
                  <label class="info-label">
                    <feather-icon
                      icon="SettingsIcon"
                      size="16"
                      class="mr-2 text-muted"
                    />
                    المواصفات الفنية
                  </label>
                  <p class="info-value">
                    {{ (engineeringProfile && engineeringProfile.project_details && engineeringProfile.project_details.technical_specifications) || 'غير محدد' }}
                  </p>
                </div>
              </div>

              <div v-else>
                <b-form-group
                  label="نطاق العمل"
                  label-for="scope_of_work"
                >
                  <b-form-textarea
                    id="scope_of_work"
                    v-model="formData.scope_of_work"
                    placeholder="أدخل نطاق العمل"
                    rows="4"
                  />
                </b-form-group>
                <b-form-group
                  label="المواصفات الفنية"
                  label-for="technical_specifications"
                >
                  <b-form-textarea
                    id="technical_specifications"
                    v-model="formData.technical_specifications"
                    placeholder="أدخل المواصفات الفنية"
                    rows="4"
                  />
                </b-form-group>
              </div>
            </b-card>
          </b-col>

          <!-- Attachments -->
          <b-col
            v-if="engineeringProfile && engineeringProfile.attachments && engineeringProfile.attachments.length > 0"
            cols="12"
          >
            <b-card class="info-card shadow-sm border-0">
              <div class="card-icon-header mb-3">
                <div class="icon-wrapper bg-warning-light">
                  <feather-icon
                    icon="PaperclipIcon"
                    size="20"
                    class="text-warning"
                  />
                </div>
                <h5 class="mb-0 font-weight-bold">
                  المرفقات
                </h5>
              </div>
              <div class="attachments-list">
                <div
                  v-for="(attachment, index) in engineeringProfile.attachments"
                  :key="index"
                  class="attachment-item d-flex align-items-center justify-content-between p-3 mb-2 border rounded"
                >
                  <div class="d-flex align-items-center">
                    <feather-icon
                      icon="FileIcon"
                      size="20"
                      class="text-primary mr-2"
                    />
                    <div>
                      <p class="mb-0 font-weight-bold">
                        {{ attachment.name }}
                      </p>
                      <small class="text-muted">{{ attachment.type || 'document' }}</small>
                    </div>
                  </div>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="viewAttachment(attachment)"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="14"
                      class="mr-1"
                    />
                    عرض
                  </b-button>
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- Location Coordinates -->
          <b-col
            v-if="engineeringProfile && engineeringProfile.location_coordinates && engineeringProfile.location_coordinates.length > 0"
            cols="12"
          >
            <b-card class="info-card shadow-sm border-0">
              <div class="card-icon-header mb-3">
                <div class="d-flex justify-content-between align-items-center w-100">
                  <div class="d-flex align-items-center">
                    <div class="icon-wrapper bg-danger-light mr-3">
                      <feather-icon
                        icon="MapIcon"
                        size="20"
                        class="text-danger"
                      />
                    </div>
                    <h5 class="mb-0 font-weight-bold">
                      إحداثيات مواقع المكتب الهندسي
                    </h5>
                  </div>
                  <div v-if="!isEditing">
                    <b-button
                      v-b-tooltip.hover.top="'رفع ملف KML لاستخراج الإحداثيات'"
                      size="sm"
                      variant="outline-primary"
                      @click="triggerKmlUpload"
                    >
                      <feather-icon
                        icon="UploadIcon"
                        size="14"
                        class="mr-1"
                      />
                      رفع ملف KML
                    </b-button>
                    <input
                      ref="kmlFileInput"
                      type="file"
                      accept=".kml,.kmz"
                      style="display: none"
                      @change="handleKmlFileUpload"
                    >
                  </div>
                </div>
              </div>

              <div v-if="!isEditing">
                <div class="coordinates-list">
                  <div
                    v-for="(locationCoord, locationIndex) in engineeringProfile.location_coordinates"
                    :key="locationIndex"
                    class="location-group mb-4"
                  >
                    <div class="location-header mb-3 p-3 bg-light rounded">
                      <h6 class="mb-0 font-weight-bold">
                        <feather-icon
                          icon="MapPinIcon"
                          size="16"
                          class="mr-2 text-primary"
                        />
                        موقع {{ locationIndex + 1 }}
                        <small
                          v-if="locationCoord.project_location_po_number"
                          class="text-muted mr-2"
                        >
                          (PO: {{ locationCoord.project_location_po_number }})
                        </small>
                      </h6>
                    </div>
                    <!-- <div class="coordinates-grid">
                      <coordinate-display-card
                        v-for="(coord, coordIndex) in getLocationCoordinates(locationCoord)"
                        :key="coordIndex"
                        :coordinate="coord"
                        :title="`نقطة ${coordIndex + 1}`"
                        :subtitle="getLocationSubtitle(locationCoord)"
                        icon="MapIcon"
                        icon-variant="bg-danger-light"
                        icon-color="danger"
                        :show-tabs="true"
                        :show-map-button="true"
                        :no-coordinates-message="'لا توجد إحداثيات مسجلة لهذه النقطة'"
                        @view-on-map="viewCoordinateOnMap"
                      />
                    </div> -->
                  </div>
                </div>
              </div>

              <div v-else>
                <div
                  v-for="(locationData, locationIndex) in formData.location_coordinates"
                  :key="locationIndex"
                  class="location-coordinate-edit mb-4 p-3 border rounded"
                >
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h6 class="mb-0 font-weight-bold">
                      <feather-icon
                        icon="MapPinIcon"
                        size="16"
                        class="mr-2 text-primary"
                      />
                      موقع {{ locationIndex + 1 }}
                      <small
                        v-if="locationData.project_location_po_number"
                        class="text-muted mr-2"
                      >
                        (PO: {{ locationData.project_location_po_number }})
                      </small>
                    </h6>
                    <b-button
                      size="sm"
                      variant="outline-success"
                      class="mr-2"
                      @click="addCoordinateToLocation(locationIndex)"
                    >
                      <feather-icon
                        icon="PlusIcon"
                        size="14"
                        class="mr-1"
                      />
                      إضافة نقطة
                    </b-button>
                  </div>

                  <div
                    v-for="(coord, coordIndex) in locationData.coordinates"
                    :key="coordIndex"
                    class="coordinate-point-edit mb-3 p-3 border rounded bg-light"
                  >
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <strong class="text-primary">نقطة {{ coordIndex + 1 }}</strong>
                      <b-button
                        v-if="locationData.coordinates.length > 1"
                        size="sm"
                        variant="outline-danger"
                        @click="removeCoordinateFromLocation(locationIndex, coordIndex)"
                      >
                        <feather-icon
                          icon="TrashIcon"
                          size="14"
                        />
                      </b-button>
                    </div>
                    <b-row class="g-2">
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="خط العرض (DD)"
                          label-for="latitude"
                        >
                          <b-form-input
                            :id="`latitude_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.latitude"
                            type="number"
                            step="any"
                            placeholder="30.0444"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="خط الطول (DD)"
                          label-for="longitude"
                        >
                          <b-form-input
                            :id="`longitude_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.longitude"
                            type="number"
                            step="any"
                            placeholder="31.2357"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="UTM East"
                          label-for="utm_east"
                        >
                          <b-form-input
                            :id="`utm_east_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.utm_east"
                            type="number"
                            step="any"
                            placeholder="350000.00"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="UTM North"
                          label-for="utm_north"
                        >
                          <b-form-input
                            :id="`utm_north_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.utm_north"
                            type="number"
                            step="any"
                            placeholder="3325000.00"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="UTM Zone"
                          label-for="utm_zone"
                        >
                          <b-form-input
                            :id="`utm_zone_${locationIndex}_${coordIndex}`"
                            v-model="coord.utm_zone"
                            placeholder="36N"
                            maxlength="10"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="ETM East"
                          label-for="etm_east"
                        >
                          <b-form-input
                            :id="`etm_east_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.etm_east"
                            type="number"
                            step="any"
                            placeholder="ETM East"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="ETM North"
                          label-for="etm_north"
                        >
                          <b-form-input
                            :id="`etm_north_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.etm_north"
                            type="number"
                            step="any"
                            placeholder="ETM North"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="ETM Zone"
                          label-for="etm_zone"
                        >
                          <b-form-input
                            :id="`etm_zone_${locationIndex}_${coordIndex}`"
                            v-model="coord.etm_zone"
                            placeholder="ETM Zone"
                            maxlength="10"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="خط العرض (DMS)"
                          label-for="dms_latitude"
                        >
                          <b-form-input
                            :id="`dms_latitude_${locationIndex}_${coordIndex}`"
                            v-model="coord.dms_latitude"
                            placeholder="30°02'39.84&quot;N"
                            maxlength="50"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="خط الطول (DMS)"
                          label-for="dms_longitude"
                        >
                          <b-form-input
                            :id="`dms_longitude_${locationIndex}_${coordIndex}`"
                            v-model="coord.dms_longitude"
                            placeholder="31°14'08.52&quot;E"
                            maxlength="50"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="X (Web Mercator)"
                          label-for="x"
                        >
                          <b-form-input
                            :id="`x_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.x"
                            type="number"
                            step="any"
                            placeholder="X coordinate"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="Y (Web Mercator)"
                          label-for="y"
                        >
                          <b-form-input
                            :id="`y_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.y"
                            type="number"
                            step="any"
                            placeholder="Y coordinate"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="نظام الإحداثيات"
                          label-for="coordinate_system"
                        >
                          <b-form-input
                            :id="`coordinate_system_${locationIndex}_${coordIndex}`"
                            v-model="coord.coordinate_system"
                            placeholder="WGS84"
                            maxlength="50"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="الدقة (متر)"
                          label-for="accuracy"
                        >
                          <b-form-input
                            :id="`accuracy_${locationIndex}_${coordIndex}`"
                            v-model.number="coord.accuracy"
                            type="number"
                            step="any"
                            min="0"
                            placeholder="5.5"
                          />
                        </b-form-group>
                      </b-col>
                      <b-col
                        cols="12"
                        sm="6"
                        md="3"
                      >
                        <b-form-group
                          label="تاريخ التسجيل"
                          label-for="recorded_at"
                        >
                          <b-form-input
                            :id="`recorded_at_${locationIndex}_${coordIndex}`"
                            v-model="coord.recorded_at"
                            type="datetime-local"
                          />
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </div>
            </b-card>
          </b-col>

          <!-- Metadata -->
          <b-col
            v-if="engineeringProfile && engineeringProfile.metadata && Object.keys(engineeringProfile.metadata).length > 0"
            cols="12"
          >
            <b-card class="info-card shadow-sm border-0">
              <div class="card-icon-header mb-3">
                <div class="icon-wrapper bg-secondary-light">
                  <feather-icon
                    icon="DatabaseIcon"
                    size="20"
                    class="text-secondary"
                  />
                </div>
                <h5 class="mb-0 font-weight-bold">
                  البيانات الإضافية
                </h5>
              </div>
              <div class="metadata-list">
                <div
                  v-for="(value, key) in engineeringProfile.metadata"
                  :key="key"
                  class="metadata-item mb-3"
                >
                  <label class="info-label">{{ formatMetadataKey(key) }}</label>
                  <p class="info-value">
                    {{ value }}
                  </p>
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- KML Upload Loading -->
    <div
      v-if="parsingKml"
      class="text-center p-3 border rounded bg-light mt-3"
    >
      <b-spinner
        variant="primary"
        class="mb-2"
      />
      <p class="text-muted mb-0">
        جاري معالجة ملف KML...
      </p>
    </div>

    <!-- KML Preview Modal -->
    <b-modal
      id="kml-preview-modal"
      v-model="showKmlPreviewModal"
      title="معاينة إحداثيات ملف KML"
      size="xl"
      centered
      cancel-title="إلغاء"
      hide-footer
      @cancel="kmlCoordinates = []"
    >
      <div
        v-if="kmlCoordinates.length === 0"
        class="text-center py-4"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="32"
          class="text-muted mb-2"
        />
        <p class="text-muted mb-0">
          لا توجد إحداثيات للعرض
        </p>
      </div>

      <div v-else>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <b-badge
            variant="success"
            class="px-3 py-2"
          >
            <feather-icon
              icon="CheckCircleIcon"
              size="14"
              class="mr-1"
            />
            تم العثور على {{ kmlCoordinates.length }} إحداثي
          </b-badge>
        </div>

        <div
          class="kml-coordinates-preview"
          style="max-height: 400px; overflow-y: auto;"
        >
          <b-table
            :items="kmlCoordinates"
            :fields="[
              { key: 'index', label: '#' },
              { key: 'latitude', label: 'خط العرض' },
              { key: 'longitude', label: 'خط الطول' },
              { key: 'actions', label: 'إجراءات' }
            ]"
            small
            striped
            responsive
            class="mb-0"
          >
            <template #cell(index)="data">
              {{ data.index + 1 }}
            </template>
            <template #cell(latitude)="data">
              <code>{{ data.item.latitude.toFixed(6) }}</code>
            </template>
            <template #cell(longitude)="data">
              <code>{{ data.item.longitude.toFixed(6) }}</code>
            </template>
            <template #cell(actions)="data">
              <b-button
                size="sm"
                variant="outline-primary"
                @click="viewCoordinateOnMap(data.item)"
              >
                <feather-icon
                  icon="MapIcon"
                  size="12"
                  class="mr-1"
                />
                عرض على الخريطة
              </b-button>
            </template>
          </b-table>
        </div>

        <div class="mt-3 p-3 bg-light rounded">
          <div class="mb-2">
            <strong class="text-primary">تطبيق الإحداثيات على موقع:</strong>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <b-button
              v-for="(location, index) in formData.location_coordinates"
              :key="index"
              size="sm"
              variant="outline-primary"
              @click="applyKmlCoordinatesToLocation(index)"
            >
              موقع {{ index + 1 }}
              <small
                v-if="location.project_location_po_number"
                class="mr-1"
              >
                (PO: {{ location.project_location_po_number }})
              </small>
            </b-button>
          </div>
          <small class="text-muted d-block mt-2">
            <feather-icon
              icon="InfoIcon"
              size="14"
              class="mr-1"
            />
            سيتم إضافة جميع الإحداثيات إلى الموقع المحدد. يمكنك إضافة المزيد من النقاط يدوياً.
          </small>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BSpinner,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea, BTable, BModal,
  VBTooltip,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'; import CoordinateDisplayCard from '@/components/CoordinateDisplayCard.vue'

export default {
  name: 'EngineeringProfile',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BSpinner,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    FeatherIcon,
    CoordinateDisplayCard,
    BTable,
    BModal,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isEditing: false,
      saving: false,
      parsingKml: false,
      showKmlPreviewModal: false,
      kmlCoordinates: [],
      formData: {
        engineering_office_name: '',
        engineering_office_license: '',
        engineering_office_contact: '',
        engineering_office_address: '',
        license_number: '',
        license_issue_date: '',
        license_expiry_date: '',
        license_authority: '',
        scope_of_work: '',
        technical_specifications: '',
        location_coordinates: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      engineeringProfile: 'projects/engineeringProfile',
      loadEngineeringProfile: 'projects/loadEngineeringProfile',
    }),
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.$store
        .dispatch('projects/getEngineeringProfile', { id: this.projectId })
        .then(() => {
          console.log('Engineering profile loaded successfully')
        })
        .catch(error => {
          console.error('Error fetching engineering profile:', error)
          this.$bvToast.toast('فشل تحميل بيانات الملف الهندسي', {
            title: 'خطأ',
            variant: 'danger',
            solid: true,
          })
        })
    },
    startEdit() {
      // Populate form with current data
      if (this.engineeringProfile) {
        // Populate location coordinates - support multiple coordinates per location
        // API returns: location_coordinates array where each item has a single "coordinate" object
        // Multiple coordinates for same location = multiple entries with same project_location_id
        const locationCoordinates = []
        const locationMap = new Map() // Group by project_location_id

        if (this.engineeringProfile.location_coordinates && Array.isArray(this.engineeringProfile.location_coordinates) && this.engineeringProfile.location_coordinates.length > 0) {
          // First pass: group coordinates by project_location_id
          this.engineeringProfile.location_coordinates.forEach(locCoord => {
            const locationId = locCoord.project_location_id || `temp_${locationCoordinates.length}`

            if (!locationMap.has(locationId)) {
              locationMap.set(locationId, {
                project_location_id: locCoord.project_location_id || null,
                project_location_po_number: locCoord.project_location_po_number || null,
                project_location_land_number: locCoord.project_location_land_number || null,
                coordinates: [],
              })
            }

            const location = locationMap.get(locationId)

            // Extract coordinate (could be single coordinate object or array)
            let coord = null
            if (locCoord.coordinate) {
              coord = locCoord.coordinate
            } else if (Array.isArray(locCoord.coordinates) && locCoord.coordinates.length > 0) {
              coord = locCoord.coordinates[0] // Take first if array
            }

            if (coord) {
              location.coordinates.push({
                id: coord.id || null,
                latitude: coord.latitude || null,
                longitude: coord.longitude || null,
                utm_east: coord.utm_east || null,
                utm_north: coord.utm_north || null,
                utm_zone: coord.utm_zone || null,
                etm_east: coord.etm_east || null,
                etm_north: coord.etm_north || null,
                etm_zone: coord.etm_zone || null,
                dms_latitude: coord.dms_latitude || null,
                dms_longitude: coord.dms_longitude || null,
                x: coord.x || null,
                y: coord.y || null,
                coordinate_system: coord.coordinate_system || 'WGS84',
                accuracy: coord.accuracy || null,
                recorded_at: coord.recorded_at ? this.formatDateTimeLocal(coord.recorded_at) : null,
              })
            }
          })

          // Convert map to array
          locationMap.forEach(location => {
            // If no coordinates, add empty one
            if (location.coordinates.length === 0) {
              location.coordinates.push(this.getEmptyCoordinate())
            }
            locationCoordinates.push(location)
          })
        }

        this.formData = {
          engineering_office_name:
            (this.engineeringProfile && this.engineeringProfile.engineering_office && this.engineeringProfile.engineering_office.name) || '',
          engineering_office_license:
            (this.engineeringProfile && this.engineeringProfile.engineering_office && this.engineeringProfile.engineering_office.license) || '',
          engineering_office_contact:
            (this.engineeringProfile && this.engineeringProfile.engineering_office && this.engineeringProfile.engineering_office.contact) || '',
          engineering_office_address:
            (this.engineeringProfile && this.engineeringProfile.engineering_office && this.engineeringProfile.engineering_office.address) || '',
          license_number:
            (this.engineeringProfile && this.engineeringProfile.licensing && this.engineeringProfile.licensing.license_number) || '',
          license_issue_date:
            (this.engineeringProfile && this.engineeringProfile.licensing && this.engineeringProfile.licensing.issue_date) || '',
          license_expiry_date:
            (this.engineeringProfile && this.engineeringProfile.licensing && this.engineeringProfile.licensing.expiry_date) || '',
          license_authority:
            (this.engineeringProfile && this.engineeringProfile.licensing && this.engineeringProfile.licensing.authority) || '',
          scope_of_work:
            (this.engineeringProfile && this.engineeringProfile.project_details && this.engineeringProfile.project_details.scope_of_work) || '',
          technical_specifications:
            (this.engineeringProfile && this.engineeringProfile.project_details && this.engineeringProfile.project_details.technical_specifications) || '',
          location_coordinates: locationCoordinates,
        }
      }
      this.isEditing = true
    },
    cancelEdit() {
      this.isEditing = false
      this.formData = {
        engineering_office_name: '',
        engineering_office_license: '',
        engineering_office_contact: '',
        engineering_office_address: '',
        license_number: '',
        license_issue_date: '',
        license_expiry_date: '',
        license_authority: '',
        scope_of_work: '',
        technical_specifications: '',
        location_coordinates: [],
      }
    },
    getEmptyCoordinate() {
      return {
        latitude: null,
        longitude: null,
        utm_east: null,
        utm_north: null,
        utm_zone: null,
        etm_east: null,
        etm_north: null,
        etm_zone: null,
        dms_latitude: null,
        dms_longitude: null,
        x: null,
        y: null,
        coordinate_system: 'WGS84',
        accuracy: null,
        recorded_at: null,
      }
    },
    addCoordinateToLocation(locationIndex) {
      if (this.formData.location_coordinates[locationIndex]) {
        if (!this.formData.location_coordinates[locationIndex].coordinates) {
          this.$set(this.formData.location_coordinates[locationIndex], 'coordinates', [])
        }
        this.formData.location_coordinates[locationIndex].coordinates.push(this.getEmptyCoordinate())
      }
    },
    removeCoordinateFromLocation(locationIndex, coordIndex) {
      if (this.formData.location_coordinates[locationIndex]
          && this.formData.location_coordinates[locationIndex].coordinates) {
        this.formData.location_coordinates[locationIndex].coordinates.splice(coordIndex, 1)
      }
    },
    getLocationCoordinates(locationCoord) {
      // Return array of coordinates for a location
      if (Array.isArray(locationCoord.coordinates)) {
        return locationCoord.coordinates
      } if (locationCoord.coordinate) {
        // Backward compatibility - single coordinate
        return [locationCoord.coordinate]
      }
      return []
    },
    saveProfile() {
      this.saving = true

      // Prepare the data for API
      const updateData = { ...this.formData }

      // Clean location_coordinates - handle multiple coordinates per location
      // API expects: location_coordinates array where each item has a single "coordinate" object
      // For multiple coordinates per location, we create multiple location entries with same project_location_id
      if (updateData.location_coordinates && Array.isArray(updateData.location_coordinates)) {
        const flattenedLocations = []

        updateData.location_coordinates.forEach(locationData => {
          // Process coordinates array - create one location entry per coordinate
          if (locationData.coordinates && Array.isArray(locationData.coordinates)) {
            locationData.coordinates.forEach(coord => {
              const cleanedCoord = {}

              // Only include non-null, non-empty coordinate fields
              if (coord.latitude !== null && coord.latitude !== undefined && coord.latitude !== '') {
                cleanedCoord.latitude = coord.latitude
              }
              if (coord.longitude !== null && coord.longitude !== undefined && coord.longitude !== '') {
                cleanedCoord.longitude = coord.longitude
              }
              if (coord.utm_east !== null && coord.utm_east !== undefined && coord.utm_east !== '') {
                cleanedCoord.utm_east = coord.utm_east
              }
              if (coord.utm_north !== null && coord.utm_north !== undefined && coord.utm_north !== '') {
                cleanedCoord.utm_north = coord.utm_north
              }
              if (coord.utm_zone) {
                cleanedCoord.utm_zone = coord.utm_zone
              }
              if (coord.etm_east !== null && coord.etm_east !== undefined && coord.etm_east !== '') {
                cleanedCoord.etm_east = coord.etm_east
              }
              if (coord.etm_north !== null && coord.etm_north !== undefined && coord.etm_north !== '') {
                cleanedCoord.etm_north = coord.etm_north
              }
              if (coord.etm_zone) {
                cleanedCoord.etm_zone = coord.etm_zone
              }
              if (coord.dms_latitude) {
                cleanedCoord.dms_latitude = coord.dms_latitude
              }
              if (coord.dms_longitude) {
                cleanedCoord.dms_longitude = coord.dms_longitude
              }
              if (coord.x !== null && coord.x !== undefined && coord.x !== '') {
                cleanedCoord.x = coord.x
              }
              if (coord.y !== null && coord.y !== undefined && coord.y !== '') {
                cleanedCoord.y = coord.y
              }
              if (coord.coordinate_system) {
                cleanedCoord.coordinate_system = coord.coordinate_system
              }
              if (coord.accuracy !== null && coord.accuracy !== undefined && coord.accuracy !== '') {
                cleanedCoord.accuracy = coord.accuracy
              }
              if (coord.recorded_at) {
                // Convert datetime-local format to ISO string
                cleanedCoord.recorded_at = new Date(coord.recorded_at).toISOString()
              }

              // Only add if coordinate has at least one field
              if (Object.keys(cleanedCoord).length > 0) {
                const locationEntry = {
                  coordinate: cleanedCoord,
                }

                // Include project_location_id if it exists
                if (locationData.project_location_id) {
                  locationEntry.project_location_id = locationData.project_location_id
                }

                // Include project_location_po_number if it exists
                if (locationData.project_location_po_number) {
                  locationEntry.project_location_po_number = locationData.project_location_po_number
                }

                flattenedLocations.push(locationEntry)
              }
            })
          }
        })

        updateData.location_coordinates = flattenedLocations
      }

      this.$store
        .dispatch('projects/updateEngineeringProfile', {
          id: this.projectId,
          query: updateData,
        })
        .then(() => {
          this.$bvToast.toast('تم حفظ بيانات الملف الهندسي بنجاح', {
            title: 'نجاح',
            variant: 'success',
            solid: true,
          })
          this.isEditing = false
          this.saving = false
          // Reload the profile to get updated data
          this.init()
        })
        .catch(error => {
          console.error('Error updating engineering profile:', error)
          let errorMessage = 'فشل حفظ بيانات الملف الهندسي'
          if (error.response && error.response.data && error.response.data.errors) {
            const { errors } = error.response.data
            const firstError = Object.values(errors)[0]
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
          }
          this.$bvToast.toast(errorMessage, {
            title: 'خطأ',
            variant: 'danger',
            solid: true,
          })
          this.saving = false
        })
    },
    formatDate(dateString) {
      if (!dateString) return null
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('ar-EG')
      } catch (e) {
        return dateString
      }
    },
    formatDateTimeLocal(dateString) {
      if (!dateString) return null
      try {
        const date = new Date(dateString)
        // Format as YYYY-MM-DDTHH:mm for datetime-local input
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day}T${hours}:${minutes}`
      } catch (e) {
        return null
      }
    },
    formatMetadataKey(key) {
      // Convert snake_case or camelCase to readable Arabic text
      const keyMap = {
        last_reviewed: 'آخر مراجعة',
        reviewed_by: 'مراجع بواسطة',
        review_status: 'حالة المراجعة',
        notes: 'ملاحظات',
      }
      return keyMap[key] || key
    },
    viewAttachment(attachment) {
      // Open attachment in new window or download
      if (attachment.path) {
        window.open(attachment.path, '_blank')
      }
    },
    getLocationSubtitle(locationCoord) {
      const parts = []
      if (locationCoord.project_location_po_number) {
        parts.push(`PO: ${locationCoord.project_location_po_number}`)
      }
      if (locationCoord.project_location_land_number) {
        parts.push(`القطعة: ${locationCoord.project_location_land_number}`)
      }
      return parts.length > 0 ? parts.join(' | ') : ''
    },
    viewCoordinateOnMap(coordinate) {
      // Open coordinate on map
      if (coordinate.latitude && coordinate.longitude) {
        const url = `https://www.google.com/maps?q=${coordinate.latitude},${coordinate.longitude}`
        window.open(url, '_blank')
      } else {
        this.$bvToast.toast('لا يمكن عرض الإحداثيات على الخريطة - البيانات غير مكتملة', {
          title: 'تحذير',
          variant: 'warning',
          solid: true,
        })
      }
    },
    triggerKmlUpload() {
      const fileInput = this.$refs.kmlFileInput
      if (fileInput) {
        fileInput.click()
      }
    },
    async handleKmlFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // Validate file type
      const fileName = file.name.toLowerCase()
      if (!fileName.endsWith('.kml') && !fileName.endsWith('.kmz')) {
        this.$bvToast.toast('يرجى اختيار ملف KML أو KMZ صحيح', {
          title: 'خطأ',
          variant: 'danger',
          solid: true,
        })
        return
      }

      this.parsingKml = true

      try {
        const text = await this.readFileAsText(file)
        const coordinates = this.parseKmlFile(text)

        if (coordinates.length === 0) {
          this.$bvToast.toast('لم يتم العثور على إحداثيات في ملف KML', {
            title: 'تحذير',
            variant: 'warning',
            solid: true,
          })
          this.parsingKml = false
          return
        }

        // Check if we have location_coordinates structure
        if (!this.formData.location_coordinates || this.formData.location_coordinates.length === 0) {
          // Initialize with empty coordinates for each location
          const locationCount = this.engineeringProfile?.location_coordinates?.length || 1
          this.formData.location_coordinates = Array(locationCount).fill(null).map((_, index) => ({
            project_location_id: this.engineeringProfile?.location_coordinates?.[index]?.project_location_id || null,
            project_location_po_number: this.engineeringProfile?.location_coordinates?.[index]?.project_location_po_number || null,
            coordinates: [this.getEmptyCoordinate()],
          }))
        }

        // Ensure all locations have coordinates array
        this.formData.location_coordinates.forEach(locationData => {
          if (!locationData.coordinates || !Array.isArray(locationData.coordinates)) {
            this.$set(locationData, 'coordinates', locationData.latitude ? [{
              latitude: locationData.latitude,
              longitude: locationData.longitude,
              utm_east: locationData.utm_east,
              utm_north: locationData.utm_north,
              utm_zone: locationData.utm_zone,
              etm_east: locationData.etm_east,
              etm_north: locationData.etm_north,
              etm_zone: locationData.etm_zone,
              dms_latitude: locationData.dms_latitude,
              dms_longitude: locationData.dms_longitude,
              x: locationData.x,
              y: locationData.y,
              coordinate_system: locationData.coordinate_system || 'WGS84',
              accuracy: locationData.accuracy,
              recorded_at: locationData.recorded_at,
            }] : [this.getEmptyCoordinate()])
          }
        })

        // If only one coordinate, add to first location's coordinates array
        if (coordinates.length === 1 && this.formData.location_coordinates.length > 0) {
          const coord = coordinates[0]
          if (!this.formData.location_coordinates[0].coordinates) {
            this.$set(this.formData.location_coordinates[0], 'coordinates', [])
          }
          this.formData.location_coordinates[0].coordinates.push({
            latitude: coord.latitude,
            longitude: coord.longitude,
            coordinate_system: coord.coordinate_system || 'WGS84',
          })

          this.$bvToast.toast('تم استخراج الإحداثيات بنجاح. يرجى حفظ التغييرات.', {
            title: 'نجاح',
            variant: 'success',
            solid: true,
          })
        } else {
          // Show preview modal for multiple coordinates
          this.kmlCoordinates = coordinates
          this.showKmlPreviewModal = true
        }
      } catch (error) {
        console.error('Error parsing KML file:', error)
        this.$bvToast.toast('فشل في قراءة ملف KML', {
          title: 'خطأ',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.parsingKml = false
        event.target.value = ''
      }
    },
    readFileAsText(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = e => resolve(e.target.result)
        reader.onerror = e => reject(e)
        reader.readAsText(file)
      })
    },
    parseKmlFile(kmlText) {
      const coordinates = []

      try {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(kmlText, 'text/xml')

        const parserError = xmlDoc.querySelector('parsererror')
        if (parserError) {
          throw new Error('Invalid KML file format')
        }

        const coordinateElements = xmlDoc.getElementsByTagName('coordinates')

        for (let i = 0; i < coordinateElements.length; i++) {
          const coordText = coordinateElements[i].textContent.trim()
          const coordPairs = coordText.split(/\s+/)

          coordPairs.forEach(pair => {
            if (!pair) return

            const parts = pair.split(',')
            if (parts.length >= 2) {
              const longitude = parseFloat(parts[0].trim())
              const latitude = parseFloat(parts[1].trim())

              if (!isNaN(latitude) && !isNaN(longitude)) {
                coordinates.push({
                  latitude,
                  longitude,
                  coordinate_system: 'WGS84',
                })
              }
            }
          })
        }

        const pointElements = xmlDoc.getElementsByTagName('Point')
        for (let i = 0; i < pointElements.length; i++) {
          const coordinatesTag = pointElements[i].getElementsByTagName('coordinates')[0]
          if (coordinatesTag) {
            const coordText = coordinatesTag.textContent.trim()
            const parts = coordText.split(',')
            if (parts.length >= 2) {
              const longitude = parseFloat(parts[0].trim())
              const latitude = parseFloat(parts[1].trim())

              if (!isNaN(latitude) && !isNaN(longitude)) {
                const exists = coordinates.some(coord => Math.abs(coord.latitude - latitude) < 0.0001
                  && Math.abs(coord.longitude - longitude) < 0.0001)

                if (!exists) {
                  coordinates.push({
                    latitude,
                    longitude,
                    coordinate_system: 'WGS84',
                  })
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error parsing KML:', error)
        throw error
      }

      return coordinates
    },
    applyKmlCoordinatesToLocation(locationIndex) {
      if (locationIndex >= 0 && locationIndex < this.formData.location_coordinates.length && this.kmlCoordinates.length > 0) {
        // Ensure coordinates array exists
        if (!this.formData.location_coordinates[locationIndex].coordinates) {
          this.$set(this.formData.location_coordinates[locationIndex], 'coordinates', [])
        }

        const coordinatesCount = this.kmlCoordinates.length

        // Add all remaining coordinates to this location
        this.kmlCoordinates.forEach(coord => {
          this.formData.location_coordinates[locationIndex].coordinates.push({
            latitude: coord.latitude,
            longitude: coord.longitude,
            coordinate_system: coord.coordinate_system || 'WGS84',
          })
        })

        // Clear the KML coordinates and close modal
        this.kmlCoordinates = []
        this.showKmlPreviewModal = false

        this.$bvToast.toast(`تم إضافة ${coordinatesCount} إحداثي إلى الموقع ${locationIndex + 1}`, {
          title: 'نجاح',
          variant: 'success',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-primary-light {
  background-color: rgba(102, 126, 234, 0.1);
}

.bg-success-light {
  background-color: rgba(40, 199, 111, 0.1);
}

.bg-info-light {
  background-color: rgba(13, 202, 240, 0.1);
}

.bg-warning-light {
  background-color: rgba(255, 193, 7, 0.1);
}

.bg-secondary-light {
  background-color: rgba(108, 117, 125, 0.1);
}

.bg-danger-light {
  background-color: rgba(234, 84, 85, 0.1);
}

.info-card {
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  }
}

.card-icon-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
}

.info-item {
  padding: 12px 0;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
}

.info-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 8px;
}

.info-value {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.6;
  margin: 0;
  padding-right: 24px;
}

.attachment-item {
  transition: all 0.3s ease;

  &:hover {
    background-color: #f8f9fa;
    transform: translateX(-4px);
  }
}

.metadata-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
}

.g-4 {
  --bs-gutter-x: 1.5rem;
  --bs-gutter-y: 1.5rem;
}

.location-coordinate-item {
  background: #f8f9fa;
  transition: all 0.3s ease;

  &:hover {
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.location-coordinate-edit {
  background: #ffffff;
  border: 2px solid #e9ecef !important;
}

.coordinates-grid {
  margin-top: 12px;
}

.coordinate-field {
  padding: 8px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.coordinate-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  margin-bottom: 4px;
  display: block;
}

.coordinate-value {
  font-size: 0.875rem;
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

.location-info {
  margin-top: 8px;
}

.coordinates-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
